import { useQuery } from "@tanstack/react-query";
import {
  getBehaviorIncidentSubTypes,
} from "../../../../api/incident/apiIncidents";
import { useMemo } from "react";
import { SelectOption } from "src/store/analytics/storeAnalytics.types";
import useFormDataHelpers from "../../../../hooks/useFormDataHelpers";

interface BehaviorAnalysisOptions {
  behaviorIncidentTypeOptions: SelectOption[];
  behaviorIncidentSubTypeOptions: SelectOption[];
  isBehaviorIncidentTypesLoading: boolean;
  isBehaviorIncidentSubTypesLoading: boolean;
  isBehaviorIncidentTypesError: boolean;
  isBehaviorIncidentSubTypesError: boolean;
}

interface UseBehaviorAnalysisOptionsParams {
  selectedFacility?: string;
}

export const ALL_BEHAVIOR_INCIDENT_TYPES_OPTION = {
  label: "All Behavior Incident Types",
  value: "all_behavior_incident_types",
};

export const ALL_BEHAVIOR_INCIDENT_SUB_TYPES_OPTION = {
  label: "All Behavior Incident Sub Types",
  value: "all_behavior_incident_sub_types",
};

export const ALL_LOCATION_UNIT_OPTION = {
  label: "All Location Units",
  value: "all_location_units",
};

const useBehaviorAnalysisOptions = (
  params?: UseBehaviorAnalysisOptionsParams
): BehaviorAnalysisOptions => {
  const { selectedFacility } = params || {};
  const { toFormattedSelectOptions, removeDuplicateOptions } =
    useFormDataHelpers();
  // Behavior incident types are hardcoded below, so no API query is needed
  const isBehaviorIncidentTypesLoading = false;
  const isBehaviorIncidentTypesError = false;

  const {
    isLoading: isBehaviorIncidentSubTypesLoading,
    isError: isBehaviorIncidentSubTypesError,
    data: behaviorIncidentSubTypesData,
  } = useQuery(
    ["behaviorIncidentSubTypes"],
    () => getBehaviorIncidentSubTypes(),
    {
      keepPreviousData: true,
    }
  );


  // Behavior Type Options - hardcoded to Alleged Abuse and Behavioral Problem
  const behaviorIncidentTypeOptions = useMemo(() => {
    return [
      ALL_BEHAVIOR_INCIDENT_TYPES_OPTION,
      { label: "Alleged Abuse", value: "Alleged Abuse" },
      { label: "Behavioral Problem", value: "Behavioral Problem" },
    ];
  }, []);

  // Incident Sub Type Options
  const behaviorIncidentSubTypeOptions = useMemo(() => {
    if (isBehaviorIncidentSubTypesLoading || isBehaviorIncidentSubTypesError) {
      return [ALL_BEHAVIOR_INCIDENT_SUB_TYPES_OPTION];
    }
    const rawData = behaviorIncidentSubTypesData?.data?.data || [];
    if (!rawData || !Array.isArray(rawData)) {
      return [ALL_BEHAVIOR_INCIDENT_SUB_TYPES_OPTION];
    }
    const formattedOptions = rawData
      .map(toFormattedSelectOptions)
      .filter((opt): opt is SelectOption => opt !== null)
      .filter(removeDuplicateOptions());

    return [ALL_BEHAVIOR_INCIDENT_SUB_TYPES_OPTION, ...formattedOptions];
  }, [behaviorIncidentSubTypesData, isBehaviorIncidentSubTypesLoading, isBehaviorIncidentSubTypesError]);

  return {
    behaviorIncidentTypeOptions,
    behaviorIncidentSubTypeOptions,
    isBehaviorIncidentTypesLoading,
    isBehaviorIncidentSubTypesLoading,
    isBehaviorIncidentTypesError,
    isBehaviorIncidentSubTypesError,
  };
};

export default useBehaviorAnalysisOptions;
